import { type KeyboardEvent } from "react";
import type { Task, TaskId, TaskStatus } from "../../../../shared/types";
import { STATUS_LABELS } from "../../../../shared/types";

interface TaskItemHeaderProps {
  readonly task: Task;
  readonly isEditing: boolean;
  readonly editValue: string;
  readonly isDone: boolean;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onEditValueChange: (value: string) => void;
  readonly onStartEdit: () => void;
  readonly onCommitEdit: () => void;
  readonly onKeyDown: (event: KeyboardEvent) => void;
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
}

const STATUS_STYLES: Record<TaskStatus, string> = {
  todo: "bg-surface-alt text-ink-muted border border-border",
  doing:
    "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  done: "bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
};

export function TaskItemHeader({
  task,
  isEditing,
  editValue,
  isDone,
  onCycleStatus,
  onEditValueChange,
  onStartEdit,
  onCommitEdit,
  onKeyDown,
  inputRef,
}: TaskItemHeaderProps) {
  return (
    <>
      <button
        type="button"
        onClick={() => onCycleStatus(task.id)}
        aria-label={`Statut: ${STATUS_LABELS[task.status]}. Cliquer pour changer.`}
        className={`shrink-0 min-w-max px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-semibold rounded-full touch-feedback
          ${STATUS_STYLES[task.status]}
          hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent`}
      >
        {STATUS_LABELS[task.status]}
      </button>

      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onBlur={onCommitEdit}
          onKeyDown={onKeyDown}
          className="flex-1 min-h-8 px-2 text-sm bg-transparent border-b-2 border-accent
            focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={onStartEdit}
          className={`flex-1 text-left text-xs md:text-sm font-medium focus:outline-none truncate
            ${isDone ? "line-through text-ink-faint" : "text-ink"}`}
        >
          {task.title}
        </button>
      )}
    </>
  );
}
